import { Button, CircularProgress, InputAdornment, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import { FormikErrors, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable } from 'recoil';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { FormikTextField } from '../../common/formik-material-ui';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import { InfoTooltip } from './CustomCRM';

export interface ICustomCRM {
  crmName: string;
  callsWebhook: string;
  hasCustomCRM: boolean;
  smsWebhook: string;
  searchEndpoint: string;
  contactUpdateEndpoint: string;
  dispositionEndpoint: string;
  activityEndpoint: string;
  applyCustomCRMtoNewUsers: boolean;
}

export type TFormikErrorsForICustomCRM =
  | FormikErrors<{
      crmName: any;
      callsWebhook: any;
      hasCustomCRM: boolean;
      smsWebhook: any;
      searchEndpoint: any;
      contactUpdateEndpoint: any;
      dispositionEndpoint: any;
      activityEndpoint: any;
      applyCustomCRMtoNewUsers: boolean;
    }>
  | undefined;

export const webhooksComponent = (customCRM: ICustomCRM) => [
  {
    name: 'callsWebhook',
    label: 'Call Webhook',
    endpoint: customCRM.callsWebhook,
    tooltipLink: 'https://www.kixie.com/developer/calling-webhooks/',
    tooltip:
      'The call webhook url enables Kixie to send call data to you so you can log that data to your crm. Please click the tooltip to see our call webhooks documentation.',
    min: 0,
    max: 500,
  },
  {
    name: 'smsWebhook',
    label: 'SMS Webhook',
    endpoint: customCRM.smsWebhook,
    tooltipLink: 'https://www.kixie.com/developer/text-message-webhooks/',
    tooltip:
      'The sms webhook url enables Kixie to send sms data to you so you can log that data to your crm. Please click the tooltip to see our sms webhooks documentation.',
    min: 0,
    max: 500,
  },
  {
    name: 'searchEndpoint',
    label: 'Search Endpoint',
    endpoint: customCRM.searchEndpoint,
    required: true,
    tooltipLink: 'https://www.kixie.com/developer/custom-crm-integration/',
    tooltip:
      'The search endpoint enables Kixie to search your CRM for each inbound call and sms. We use the returned data to display your data in the Kixie dialer. Please click the tooltip to see our documentation for custom crm integration',
    min: 0,
    max: 500,
  },
  {
    name: 'contactUpdateEndpoint',
    label: 'Contact Endpoint',
    endpoint: customCRM.contactUpdateEndpoint,
    tooltipLink: 'https://www.kixie.com/developer/custom-crm-edit-contact/',
    tooltip:
      'The Contact Update endpoint is where Kixie will send data when an activity is scheduled. Please see our Contact Update handler documentation.',
    min: 0,
    max: 500,
  },
  {
    name: 'dispositionEndpoint',
    label: 'Call Outcome Endpoint',
    endpoint: customCRM.dispositionEndpoint,
    tooltipLink: 'https://www.kixie.com/developer/custom-crm-dispositions/',
    tooltip:
      'The Call Outcome endpoint is where Kixie will send data after a call is dispositioned. Please see our call outcome handler documentation.',
    min: 0,
    max: 500,
  },
  {
    name: 'activityEndpoint',
    label: 'Activities Endpoint',
    endpoint: customCRM.activityEndpoint,
    tooltipLink:
      'https://www.kixie.com/developer/custom-crm-schedule-activity/',
    tooltip:
      'The Activities endpoint is where Kixie will send data when an activity is scheduled. Please see our activites handler documentation.',
    min: 0,
    max: 500,
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  button: {
    height: '20px',
    marginRight: theme.spacing(1),
  },
  error: {
    color: 'red',
  },
  success: {
    color: 'green',
  },
}));

export function CustomCrmInputs(props: {
  formik: FormikProps<any>;
  setValidationOfCustomCRM: React.Dispatch<
    React.SetStateAction<ICustomCRM | undefined>
  >;
}) {
  const classes = useStyles();
  const [userLoadable, setUser] = useRecoilStateLoadable(user);
  const mutation = useMutation(
    getApiMutation(userLoadable.contents as IUserAtom, setUser)
  );
  const [testResult, setTestResult] = useState<{
    [label: string]:
      | undefined
      | {
          status: loadableEnum;
          message?: string;
        };
  }>({});
  // const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  async function testWebhookEndpoint(endpoint: string, label: string) {
    setTestResult((state) => ({
      ...state,
      [label]: {
        status: loadableEnum.LOADING,
      },
    }));
    try {
      const _response = await mutation.mutateAsync({
        endpoint: `${endpointEnum.FORWARDER}?url=${encodeURIComponent(
          endpoint
        )}`,
      });
      setTestResult((state) => ({
        ...state,
        [label]: {
          message: 'Success',
          status: loadableEnum.HAS_VALUE,
        },
      }));
      // enqueueSnackbarWithStyles({
      //   message: label + ' Success: ' + endpoint + '!',
      //   alertProps: { severity: 'success' },
      // });
    } catch (exception) {
      const axiosError = exception as AxiosError;
      const errorResponse = axiosError?.response?.data as {
        data: any;
        error: string;
        status: number;
      };
      setTestResult((state) => ({
        ...state,
        [label]: {
          message:
            'Test Failed: ' + (errorResponse?.error || axiosError.message),
          status: loadableEnum.HAS_ERROR,
        },
      }));
      // enqueueSnackbarWithStyles({
      //   message: label + ' Test Failed: ' + error.message,
      //   alertProps: { severity: 'warning' },
      // });
    }
    // force validation of customCRM
    props.setValidationOfCustomCRM(undefined);
  }

  return (
    <>
      {webhooksComponent(props.formik.values.customCRM).map((input, index) => (
        <article key={index}>
          <FormikTextField
            name={`customCRM.${input.name}`}
            fullWidth
            required={input.required}
            className={classes.field}
            formik={props.formik}
            label={input.label}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {testResult[input.label]?.status === loadableEnum.LOADING ? (
                    <CircularProgress
                      style={{ marginRight: '31px' }}
                      size={20}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      onClick={() => {
                        testWebhookEndpoint(input.endpoint, input.label);
                      }}
                      disabled={!input.endpoint}
                    >
                      Test
                    </Button>
                  )}
                  <InfoTooltip
                    onClick={() => window.open(input.tooltipLink, '_blank')}
                    title={input.tooltip}
                  />
                </InputAdornment>
              ),
              inputProps: {
                min: input.min,
                max: input.max,
              },
              readOnly: !props.formik.values.customCRM.hasCustomCRM,
            }}
            variant="outlined"
          />
          {testResult[input.label] !== undefined && (
            <LabelledOutline
              label="Result"
              className={clsx({
                [classes.error]:
                  testResult[input.label]?.status === loadableEnum.HAS_ERROR,
                [classes.success]:
                  testResult[input.label]?.status === loadableEnum.HAS_VALUE,
              })}
            >
              {testResult[input.label]?.message || 'Loading...'}
            </LabelledOutline>
          )}
        </article>
      ))}
    </>
  );
}
